import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { formatPhone, formatLocalPhone, toApiPhone } from '../lib/phone';
import { apiFetch } from '../api';
import { ui } from '../components/adminUi';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import { providerRegion, providerImage } from '../lib/providerInfo';

const SEARCH_DEBOUNCE_MS = 400;


export default function ProvidersPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const loadSeq = useRef(0);

  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Qidiruv: yozish tugagach (debounce) backend ?search= ga ketadi.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadProviders = useCallback(async () => {
    // Tez-tez o'zgargan qidiruvda eski javob yangisini bosib ketmasin.
    const seq = ++loadSeq.current;
    setFetchError('');
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await apiFetch(`/api/admin/providers${qs}`, auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (seq !== loadSeq.current) return;
      setProviders(Array.isArray(data) ? data : Array.isArray(data && data.items) ? data.items : []);
    } catch {
      if (seq !== loadSeq.current) return;
      setFetchError("Ko'rsatuvchilarni yuklashda xatolik yuz berdi. Sahifani yangilang.");
    } finally {
      if (seq === loadSeq.current) setPageLoading(false);
    }
  }, [auth.token, search]);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const loadCategories = useCallback(async () => {
    setCatLoading(true);
    try {
      const res = await apiFetch('/api/admin/service-categories', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    } finally {
      setCatLoading(false);
    }
  }, [auth.token]);

  const loadRegions = useCallback(async () => {
    setRegionsLoading(true);
    try {
      const res = await apiFetch('/api/regions', auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRegions(Array.isArray(data) ? data : []);
    } catch {
      setRegions([]);
    } finally {
      setRegionsLoading(false);
    }
  }, [auth.token]);

  const openModal = () => {
    setForm(EMPTY_FORM);
    setSelectedCategoryIds([]);
    setSelectedRegionId('');
    setFormError('');
    setShowModal(true);
    if (categories.length === 0) loadCategories();
    if (regions.length === 0) loadRegions();
  };

  const closeModal = () => setShowModal(false);

  const toggleCategory = (id) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.firstName.trim()) { setFormError("Ism maydoni majburiy"); return; }
    if (!form.lastName.trim()) { setFormError("Familiya maydoni majburiy"); return; }
    if (!form.email.trim()) { setFormError("Email maydoni majburiy"); return; }
    if (!form.phone.trim()) { setFormError("Telefon maydoni majburiy"); return; }
    if (!toApiPhone(form.phone)) { setFormError("Telefon raqami 9 raqamdan iborat bo'lishi kerak (XX XXX XX XX)"); return; }
    if (!form.password) { setFormError("Parol maydoni majburiy"); return; }
    if (selectedCategoryIds.length === 0) {
      setFormError("Kamida bitta xizmat kategoriyasini tanlang");
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/providers', auth.token, {
        method: 'POST',
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: toApiPhone(form.phone),
          password: form.password,
          categoryIds: selectedCategoryIds,
          ...(selectedRegionId ? { regionId: Number(selectedRegionId) } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setFormError(d.message || "Saqlashda xatolik yuz berdi");
        return;
      }
      closeModal();
      await loadProviders();
    } catch {
      setFormError("Tarmoq xatosi. Iltimos, qayta urinib ko'ring.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>Xizmat ko'rsatuvchilar</h1>
          <p style={s.subtitle}>Ro'yxatdan o'tgan xizmat ko'rsatuvchilar</p>
        </div>
        <button style={s.addBtn} onClick={openModal}>+ Qo'shish</button>
      </div>

      <div style={s.searchWrap}>
        <input
          type="search"
          style={{ ...ui.searchInput, ...s.searchInput }}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Ism, telefon yoki email bo'yicha qidirish"
          aria-label="Ustani qidirish"
        />
        {searchInput && (
          <button
            type="button"
            style={s.clearBtn}
            onClick={() => { setSearchInput(''); setSearch(''); }}
            aria-label="Tozalash"
            title="Tozalash"
          >
            <Icon name="x" size={16} />
          </button>
        )}
      </div>

      {fetchError && <div style={s.errorBanner}>{fetchError}</div>}

      {pageLoading ? (
        <div style={s.centerMsg}>Yuklanmoqda...</div>
      ) : providers.length === 0 && !fetchError ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>
            <Icon name="wrench" size={48} color="var(--color-primary)" strokeWidth={1.75} />
          </div>
          {search ? (
            <>
              <p style={s.emptyText}>Usta topilmadi</p>
              <p style={s.emptyHint}>Qidiruv so'zini o'zgartirib ko'ring.</p>
            </>
          ) : (
            <>
              <p style={s.emptyText}>Hali xizmat ko'rsatuvchi yo'q</p>
              <p style={s.emptyHint}>Birinchi ko'rsatuvchini qo'shish uchun "+ Qo'shish" tugmasini bosing.</p>
            </>
          )}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table className="th-table-fit" style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '40px' }}>#</th>
                <th className="th-col-secondary" style={{ ...s.th, width: '56px' }}>Rasm</th>
                <th style={s.th}>F.I.O</th>
                <th className="th-col-secondary" style={s.th}>Email</th>
                <th style={s.th}>Telefon</th>
                <th style={s.th}>Xizmat kategoriyalari</th>
                <th style={s.th}>Hudud</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p, i) => (
                <tr
                  key={p.id}
                  style={{ ...s.tr, cursor: 'pointer' }}
                  tabIndex={0}
                  onClick={() => navigate(`/admin/providers/${p.id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/admin/providers/${p.id}`); }}
                >
                  <td style={{ ...s.td, color: 'var(--color-text-muted)' }}>{i + 1}</td>
                  <td className="th-col-secondary" style={s.td}>
                    <Avatar src={providerImage(p)} firstName={p.firstName} lastName={p.lastName} size={40} />
                  </td>
                  <td style={{ ...s.td, fontWeight: '600', color: 'var(--color-text)' }}>
                    <Link
                      to={`/admin/providers/${p.id}`}
                      style={s.nameLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {[p.firstName, p.lastName].filter(Boolean).join(' ') || '—'}
                    </Link>
                  </td>
                  <td className="th-col-secondary" style={{ ...s.td, color: 'var(--color-text)' }}>{p.email || '—'}</td>
                  <td style={{ ...s.td, color: 'var(--color-text)' }}>{formatPhone(p.phone) || '—'}</td>
                  <td style={s.td}>
                    <CategoryBadges categories={p.categories} />
                  </td>
                  <td style={{ ...s.td, color: 'var(--color-text)' }}>{providerRegion(p) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.modalTitle}>Yangi xizmat ko'rsatuvchi</h2>

            <div style={s.row2}>
              <Field label="Ism" required>
                <input
                  style={s.input}
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  placeholder="Ism"
                  disabled={saving}
                />
              </Field>
              <Field label="Familiya" required>
                <input
                  style={s.input}
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  placeholder="Familiya"
                  disabled={saving}
                />
              </Field>
            </div>

            <Field label="Email" required>
              <input
                type="email"
                style={s.input}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="example@email.com"
                disabled={saving}
              />
            </Field>

            <Field label="Telefon (+998)" required>
              <input
                style={s.input}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: formatLocalPhone(e.target.value) })}
                onPaste={(e) => {
                  e.preventDefault();
                  setForm({ ...form, phone: formatLocalPhone(e.clipboardData.getData('text')) });
                }}
                inputMode="tel"
                placeholder="90 123 45 67"
                disabled={saving}
              />
            </Field>

            <Field label="Parol" required>
              <input
                type="password"
                style={s.input}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Kamida 6 ta belgi"
                disabled={saving}
              />
            </Field>

            <Field label="Xizmat kategoriyalari" required>
              {catLoading ? (
                <div style={s.catLoading}>Kategoriyalar yuklanmoqda...</div>
              ) : categories.length === 0 ? (
                <div style={s.catEmpty}>Kategoriya yo'q — avval xizmat kategoriyalarini qo'shing</div>
              ) : (
                <div style={s.checkList}>
                  {categories.map((cat) => {
                    const checked = selectedCategoryIds.includes(cat.id);
                    return (
                      <label
                        key={cat.id}
                        style={{ ...s.checkItem, ...(checked ? s.checkItemActive : {}) }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(cat.id)}
                          disabled={saving}
                          style={s.checkbox}
                        />
                        <span style={s.checkLabel}>{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </Field>

            <Field label="Hudud">
              {regionsLoading ? (
                <div style={s.catLoading}>Yuklanmoqda...</div>
              ) : (
                <select
                  style={s.select}
                  value={selectedRegionId}
                  onChange={(e) => setSelectedRegionId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Hududni tanlang</option>
                  {regions.length === 0 ? (
                    <option value="" disabled>Hududlar topilmadi</option>
                  ) : (
                    regions.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))
                  )}
                </select>
              )}
            </Field>

            {formError && <div style={s.formError}>{formError}</div>}

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={closeModal} disabled={saving}>
                Bekor qilish
              </button>
              <button
                style={{ ...s.saveBtn, ...(saving ? s.disabledBtn : {}) }}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Saqlanmoqda..." : "Saqlash"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={s.label}>
        {label}{required && <span style={s.req}> *</span>}
      </label>
      {children}
    </div>
  );
}

function CategoryBadges({ categories }) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return <span style={{ fontStyle: 'italic', color: 'var(--color-text-muted)' }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {categories.map((cat) => (
        <span key={cat.id} style={s.badge}>{cat.name}</span>
      ))}
    </div>
  );
}

const EMPTY_FORM = { firstName: '', lastName: '', email: '', phone: '', password: '' };

const s = {
  pageHeader: ui.pageHeader,
  title: ui.title,
  subtitle: ui.subtitle,
  addBtn: ui.addBtn,
  searchWrap: { position: 'relative', maxWidth: '480px', marginBottom: '16px' },
  searchInput: { width: '100%', minWidth: 0, paddingRight: '44px' },
  clearBtn: {
    position: 'absolute',
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
    width: '32px',
    height: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: 'var(--color-text-muted)',
    border: 'none',
    borderRadius: 'var(--radius-btn)',
    cursor: 'pointer',
    padding: 0,
  },
  errorBanner: ui.errorBanner,
  centerMsg: ui.centerMsg,
  empty: ui.empty,
  emptyIcon: ui.emptyIcon,
  emptyText: ui.emptyText,
  emptyHint: ui.emptyHint,
  tableWrap: ui.tableWrap,
  table: ui.table,
  th: ui.th,
  tr: ui.tr,
  td: ui.td,
  nameLink: { color: 'inherit', fontWeight: '700', textDecoration: 'underline', textUnderlineOffset: '3px' },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    background: 'var(--color-dark)',
    color: 'var(--color-on-dark)',
    borderRadius: 'var(--radius-chip)',
    fontSize: '12px',
    fontWeight: '500',
  },
  overlay: ui.overlay,
  modalCard: { ...ui.modalCard, maxWidth: '540px' },
  modalTitle: ui.modalTitle,
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  label: ui.label,
  req: ui.req,
  input: ui.input,
  select: { ...ui.select, appearance: 'none' },
  catLoading: { fontSize: '13px', color: 'var(--color-text-muted)', padding: '10px 0' },
  catEmpty: {
    fontSize: '13px',
    color: 'var(--color-text)',
    background: 'var(--color-primary)',
    borderRadius: 'var(--radius-input)',
    padding: '10px 12px',
  },
  checkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '200px',
    overflowY: 'auto',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-input)',
    padding: '8px',
  },
  checkItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 10px',
    borderRadius: 'var(--radius-btn)',
    cursor: 'pointer',
    background: 'var(--color-surface-alt)',
    userSelect: 'none',
  },
  checkItemActive: {
    background: 'var(--color-primary)',
  },
  checkbox: { width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-dark)' },
  checkLabel: { fontSize: '14px', color: 'var(--color-text)' },
  formError: ui.formError,
  modalActions: ui.modalActions,
  cancelBtn: ui.cancelBtn,
  saveBtn: ui.saveBtn,
  disabledBtn: ui.btnDisabled
};
